export interface ExportDimensions {
  width: number;
  height: number;
}

export interface ExportLayoutBase {
  moleculeWidth: number;
  moleculeHeight: number;
  gap: number;
  padding: number;
}

export const REACTION_LAYOUT_BASE: ExportLayoutBase = {
  moleculeWidth: 192,
  moleculeHeight: 144,
  /** No horizontal gutter: reactant, arrow, and product share the full stage width. */
  gap: 0,
  padding: 0,
};

export const MOLECULE_LAYOUT_BASE: ExportLayoutBase = {
  moleculeWidth: 192,
  moleculeHeight: 192,
  gap: 0,
  padding: 0,
};

export const DEFAULT_REACTION_EXPORT_DIMENSIONS: ExportDimensions = {
  width: 640,
  height: 240,
};

export const DEFAULT_MOLECULE_EXPORT_DIMENSIONS: ExportDimensions = {
  width: 320,
  height: 320,
};

export interface ReactionNaturalLayout {
  naturalWidth: number;
  naturalHeight: number;
  arrowHeight: number;
}

export function getReactionNaturalLayout(
  arrowWidth: number,
  reactantZoom: number,
  base: ExportLayoutBase = REACTION_LAYOUT_BASE
): ReactionNaturalLayout {
  const naturalWidth =
    base.padding +
    base.moleculeWidth +
    base.gap +
    arrowWidth +
    base.gap +
    base.moleculeWidth +
    base.padding;
  const naturalHeight = base.padding + base.moleculeHeight + base.padding;
  return {
    naturalWidth,
    naturalHeight,
    arrowHeight: 14 * reactantZoom,
  };
}

export function getMoleculeNaturalLayout(
  base: ExportLayoutBase = MOLECULE_LAYOUT_BASE
): { naturalWidth: number; naturalHeight: number } {
  return {
    naturalWidth: base.padding + base.moleculeWidth + base.padding,
    naturalHeight: base.padding + base.moleculeHeight + base.padding,
  };
}

export function parseViewBoxDimensions(
  viewBox: string | undefined
): { w: number; h: number } | null {
  if (!viewBox) return null;
  const parts = viewBox.trim().split(/\s+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  const [, , vbW, vbH] = parts;
  if (vbW <= 0 || vbH <= 0) return null;
  return { w: vbW, h: vbH };
}

/**
 * Uniform scale from SVG viewBox to slot (matches InteractiveMolecule: min(slotW/vbW, slotH/vbH)).
 */
export function getUniformSvgScale(
  viewBox: string | undefined,
  slotWidth: number,
  slotHeight: number
): number {
  const d = parseViewBoxDimensions(viewBox);
  if (!d) return 1;
  const w = Math.max(1, slotWidth);
  const h = Math.max(1, slotHeight);
  return Math.min(w / d.w, h / d.h);
}

export function getFittedSceneTransform(
  canvas: ExportDimensions,
  naturalWidth: number,
  naturalHeight: number
): { scale: number; offsetX: number; offsetY: number } {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return { scale: 1, offsetX: 0, offsetY: 0 };
  }
  const scale = Math.min(canvas.width / naturalWidth, canvas.height / naturalHeight);
  const scaledWidth = naturalWidth * scale;
  const scaledHeight = naturalHeight * scale;
  return {
    scale,
    offsetX: (canvas.width - scaledWidth) / 2,
    offsetY: (canvas.height - scaledHeight) / 2,
  };
}
